import {isKoreanIncluded} from '@shared/helpers/lang'

import {
    DELIVERY_VALIDATION_RESULT_TYPE,
    DeliveryValidationResultTypeValue,
    EntryMethodType,
    LOT_NUMBER_ADDRESS_ZIPCODE_LENGTH,
    MaxLength,
    PickUpLocationType,
} from '@web/core/constants'

import {
    getPhoneNumberPrefix,
    isUserPhoneNumberInBlacklist,
    isValidPhoneNumberPrefix,
    isValidUserPhoneNumber,
    isVirtualPhoneNumberUnavailable,
} from './helpers/number'

type AddressInfo = {
    /** 배송지 키 값 */
    hash: string
    /** 배송지 명 */
    addressName: string
    /** 수신자 명 */
    receiverName: string
    /** 우편 번호 */
    zipCode: string
    /** 기본 배송지 */
    baseAddress: string
    /** 상세 배송지 */
    detailAddress: Maybe<string>
    /** 도로명주소 여부 */
    isRoadNameAddress: boolean
    /** 연락처1 (- 포함) */
    telNo1: string
    /** 연락처2 (- 포함) */
    telNo2: Maybe<string>
    /** 기본 배송지 여부 */
    isPrimaryAddress: boolean
    /** 등록배송지 여부 */
    isRegistered: boolean
    /** 수령위치 코드 */
    locationType: Maybe<PickUpLocationType>
    /** 수령위치 설명 */
    locationDescription: Maybe<string>
    /** 출입코드 */
    entryMethodType: Maybe<EntryMethodType>
    /** 출입코드 설명 */
    entryDescription: Maybe<string>
}

/** 주소 검증 결과 타입 */
const VALIDATION_RESULT_TYPE = DELIVERY_VALIDATION_RESULT_TYPE

type ValidationResultTypeValue = DeliveryValidationResultTypeValue

type ValidationResult = {
    type: ValidationResultTypeValue
    message: string
}

const VALIDATION_RESULT = {
    /** 기본주소에 한글이 0자일 때 */
    NO_KOREAN_LETTER_IN_BASE_ADDRESS: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '정확한 배송을 위해 배송지를 한글명으로 변경해주세요.',
    },
    /** 지번주소인데 상세주소가 없을 때 */
    NO_DETAIL_ADDRESS_IN_LOT_NUMBER_ADDRESS: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '상세주소를 입력해주세요.',
    },
    /** 도로명 주소인데 상세주소가 없을 때 */
    NO_DETAIL_ADDRESS_IN_ROAD_NAME_ADDRESS: {
        type: VALIDATION_RESULT_TYPE.WARNING,
        message: '상세주소가 입력되지 않았습니다. 그래도 진행하시겠습니까?',
    },
    /** 우편번호 6자리 인 경우 */
    LOT_NUMBER_ADDRESS_ZIPCODE: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '우편번호 6자리 배송지는 사용이 불가합니다. 정확한 배송을 위해 우편번호 5자리 배송지로 수정해주세요.',
    },
    /** 유효하지 않은 국번일 때 (TEL_NO_1) */
    UNUSABLE_PHONE_NUMBER1: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '연락처1이 부정확하거나, 지원하지 않는 국번을 사용중입니다. 연락처를 수정해주세요.',
    },
    /** 유효하지 않은 국번일 때 (TEL_NO_2) */
    UNUSABLE_PHONE_NUMBER2: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '연락처2이 부정확하거나, 지원하지 않는 국번을 사용중입니다. 연락처를 수정해주세요.',
    },
    /** 수령인명 50자 초과 */
    LONG_RECEIVER_NAME: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '수령인은 최대 50자까지 입력 가능합니다.',
    },
    /** 배송지명 50자 초과 */
    LONG_ADDRESS_NAME: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '배송지명은 최대 50자까지 입력 가능합니다.',
    },
    /** 상세주소 50자 초과 */
    LONG_DETAIL_ADDRESS: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '상세주소는 최대 50자까지 입력 가능합니다.',
    },
    /** 해외배송인데 지번주소인 경우 */
    LOT_NUMBER_ADDRESS_UNUSABLE_AT_OVERSEA_DELIVERY: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '통관진행 및 정확한 배송처리를 위해 도로명주소로 변경해주세요.',
    },
    /** 도착보장 주문서인데 우편번호 6자리인 경우 */
    LOT_NUMBER_ADDRESS_ZIPCODE_AT_ARRIVAL_GUARANTEE_DELIVERY: {
        type: VALIDATION_RESULT_TYPE.INVALID,
        message: '선택한 주소는 N배송 상품 정보를 확인할 수 없습니다. 도로명 주소로 다시 입력해주세요.',
    },
} as const

type ValidateAddressInfoParams = Pick<
    AddressInfo,
    | 'addressName'
    | 'receiverName'
    | 'zipCode'
    | 'baseAddress'
    | 'detailAddress'
    | 'isRoadNameAddress'
    | 'telNo1'
    | 'telNo2'
>

export type ValidateAddressField = keyof ValidateAddressInfoParams

/**
 * 도착보장 상품 포함 및 해외배송시 validation은 주소 저장 시에는 미수행
 * 따라서 정보가 있을때만 수행하도록 optional로 받습니다.
 */
export const validateAddress = ({
    addressInfo,
    hasArrivalGuaranteeProduct,
    isOverseasDelivery,
    ignoreField = [],
}: {
    addressInfo: ValidateAddressInfoParams
    hasArrivalGuaranteeProduct?: boolean
    isOverseasDelivery?: boolean
    ignoreField?: ValidateAddressField[]
}): ValidationResult => {
    const {baseAddress, detailAddress, isRoadNameAddress, zipCode, telNo1, telNo2, receiverName, addressName} =
        addressInfo

    const ignoreFieldSet = new Set(ignoreField)

    if (!ignoreFieldSet.has('baseAddress') && !isKoreanIncluded(baseAddress)) {
        return VALIDATION_RESULT.NO_KOREAN_LETTER_IN_BASE_ADDRESS
    }

    if (!ignoreFieldSet.has('zipCode') && zipCode.length === LOT_NUMBER_ADDRESS_ZIPCODE_LENGTH) {
        if (hasArrivalGuaranteeProduct) {
            return VALIDATION_RESULT.LOT_NUMBER_ADDRESS_ZIPCODE_AT_ARRIVAL_GUARANTEE_DELIVERY
        }

        if (isOverseasDelivery) {
            return VALIDATION_RESULT.LOT_NUMBER_ADDRESS_UNUSABLE_AT_OVERSEA_DELIVERY
        }

        return VALIDATION_RESULT.LOT_NUMBER_ADDRESS_ZIPCODE
    }

    if (!ignoreFieldSet.has('isRoadNameAddress') && !addressInfo.isRoadNameAddress && isOverseasDelivery) {
        return VALIDATION_RESULT.LOT_NUMBER_ADDRESS_UNUSABLE_AT_OVERSEA_DELIVERY
    }

    if (
        !ignoreFieldSet.has('telNo1') &&
        (isUserPhoneNumberInBlacklist(telNo1) ||
            !isValidUserPhoneNumber(telNo1) ||
            !isValidPhoneNumberPrefix(getPhoneNumberPrefix(telNo1)))
    ) {
        return VALIDATION_RESULT.UNUSABLE_PHONE_NUMBER1
    }

    if (
        !ignoreFieldSet.has('telNo2') &&
        telNo2 &&
        (isUserPhoneNumberInBlacklist(telNo2) ||
            !isValidUserPhoneNumber(telNo2) ||
            !isValidPhoneNumberPrefix(getPhoneNumberPrefix(telNo2)))
    ) {
        return VALIDATION_RESULT.UNUSABLE_PHONE_NUMBER2
    }

    if (!ignoreFieldSet.has('receiverName') && receiverName.length > MaxLength.RECEIVER_NAME) {
        return VALIDATION_RESULT.LONG_RECEIVER_NAME
    }

    if (!ignoreFieldSet.has('addressName') && addressName && addressName.length > MaxLength.ADDRESS_NAME) {
        return VALIDATION_RESULT.LONG_ADDRESS_NAME
    }

    if (!ignoreFieldSet.has('detailAddress') && detailAddress && detailAddress.length > MaxLength.DETAIL_ADDRESS) {
        return VALIDATION_RESULT.LONG_DETAIL_ADDRESS
    }

    if (!ignoreFieldSet.has('detailAddress') && !detailAddress) {
        return isRoadNameAddress
            ? VALIDATION_RESULT.NO_DETAIL_ADDRESS_IN_ROAD_NAME_ADDRESS
            : VALIDATION_RESULT.NO_DETAIL_ADDRESS_IN_LOT_NUMBER_ADDRESS
    }

    return {
        type: VALIDATION_RESULT_TYPE.VALID,
        message: '',
    }
}

type ValidateVirtualPhoneNumberAvailableParams = Pick<AddressInfo, 'telNo1' | 'telNo2'>

/** 연락처 1, 2를 받았을 때 해당번호를 안심번호로 사용할 수 있는지 여부 검증. */
export const validateVirtualPhoneNumberAvailable = ({
    telNo1,
    telNo2,
}: ValidateVirtualPhoneNumberAvailableParams): ValidationResult => {
    if (isVirtualPhoneNumberUnavailable(telNo1)) {
        return VALIDATION_RESULT.UNUSABLE_PHONE_NUMBER1
    } else if (telNo2 && isVirtualPhoneNumberUnavailable(telNo2)) {
        return VALIDATION_RESULT.UNUSABLE_PHONE_NUMBER2
    } else {
        return {type: 'VALID', message: ''}
    }
}
